import * as React from 'react';

import _ from 'lodash';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';

import { redirect } from 'bundles/authentication/shared/actions';
import AlternativeAuthenticationModal from 'bundles/authentication/shared/components/authentication-modal/AuthenticationModal';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import Modal from 'bundles/phoenix/components/Modal';
import type { AuthMode } from 'bundles/user-account/common/constants';
import {
  COMPLETE_FORM_KEY,
  EXPIRED_LINK_FORM_KEY,
  FORGOT_FORM_KEY,
  LINKED_FORM_KEY,
  LINK_FORM_KEY,
  LOGIN_FORM_KEY,
  SIGNUP_FORM_KEY,
  getFormTranslations,
} from 'bundles/user-account/common/constants';
import AuthenticationFormsContainer from 'bundles/user-account/components/AuthenticationFormsContainer';

import 'css!./__styles__/AuthenticationModal';

const modeToEventKey = {
  [COMPLETE_FORM_KEY]: 'complete_modal.open',
  [LOGIN_FORM_KEY]: 'login_modal.open',
  [SIGNUP_FORM_KEY]: 'signup_modal.open',
};

type InputProps = {
  authMode: AuthMode;
  hideNav?: boolean;
  isEnterprise?: boolean;
  /**
   * use in combination with redirectOnCloseUrl. Set to true if you want the redirect to happen via a web redirect (i.e. window.location.replace) instead of
   * a React router SPA push
   */
  utilizeWebRedirectOnClose?: boolean;
  redirectOnCloseUrl?: string;
  preventModalClose?: boolean;
  preventRegister?: boolean;
  customRedirectUrl?: string;
  switchFormsByQueryParam?: boolean;
  programId?: string;
};

type Props = InputProps & {
  router: InjectedRouter;
};

export class AuthenticationModal extends React.Component<Props> {
  static defaultProps = {
    switchFormsByQueryParam: true,
  };

  public static validModes: Array<string> = [
    SIGNUP_FORM_KEY,
    LOGIN_FORM_KEY,
    LOGIN_FORM_KEY,
    COMPLETE_FORM_KEY,
    LINK_FORM_KEY,
    LINKED_FORM_KEY,
    EXPIRED_LINK_FORM_KEY,
    FORGOT_FORM_KEY,
  ];

  componentWillMount() {
    const { authMode } = this.props;
    // there are two modals, one in react and the other one is in backbone, in order to make
    // trackng consistent, we have to keep this usage here. but makeTracker function is fully
    // deprecated, you shouldn't use the code like this in other places.

    const userModalTrack = Retracked.makeTracker({
      namespace: 'open_course.user', // for backwards compatibility with userAuthorization
    });

    // @ts-expect-error TS7016 TODO: modeToEventKey[authMode] needs to be typed
    userModalTrack(modeToEventKey[authMode]);
  }

  onClose = () => {
    const { redirectOnCloseUrl } = this.props;
    if (redirectOnCloseUrl) {
      this.redirectToUrl(redirectOnCloseUrl);
    } else {
      this.routeSansAuthMode();
    }
  };

  redirectToUrl = (pathname: string) => {
    const { router, utilizeWebRedirectOnClose } = this.props;

    if (utilizeWebRedirectOnClose) {
      window.location.replace(pathname);
    } else {
      router.push({ pathname });
    }
  };

  hasAnchor = () => {
    if (window.location.hash === '') {
      return false;
    } else {
      return true;
    }
  };

  routeSansAuthMode = () => {
    if (this.hasAnchor()) {
      redirect();
    } else {
      const { router } = this.props;
      const newQueryParams = _.omit(router.location.query, ['authMode', 'errorCode', 'completeMode', 'redirectTo']);
      router.push({
        pathname: router.location.pathname,
        params: router.params,
        query: newQueryParams,
      });
    }
  };

  render() {
    // each form has a different subset of these, but it doesn't hurt to pass them all
    const {
      hideNav,
      authMode,
      isEnterprise: isEnterpriseProp,
      switchFormsByQueryParam,
      customRedirectUrl,
      programId,
      preventModalClose,
    } = this.props;
    const FORM_TRANSLATIONS = getFormTranslations();

    let modalName = FORM_TRANSLATIONS.LOGIN;
    switch (authMode) {
      case SIGNUP_FORM_KEY:
        modalName = FORM_TRANSLATIONS.SIGN_UP;
        break;
      case COMPLETE_FORM_KEY:
        modalName = FORM_TRANSLATIONS.COMPLETE;
        break;
      default:
      // do nothing
    }

    const authFormsContainerProps = {
      hideNav,
      authMode,
      isEnterprise: isEnterpriseProp,
      switchFormsByQueryParam,
      customRedirectUrl,
      programId,
    };

    return (
      <TrackedDiv
        trackingName="authentication_modal"
        className="rc-AuthenticationModal"
        data-e2e="auth-modal"
        trackClicks={false}
        withVisibilityTracking
      >
        <Modal type="layer" modalName={modalName} handleClose={this.onClose} allowClose={!preventModalClose}>
          <AuthenticationFormsContainer {...authFormsContainerProps} />
        </Modal>
      </TrackedDiv>
    );
  }
}

export default compose<Props, InputProps>(
  connectToRouter((router) => ({ router })),
  (WrappedComponent: React.ComponentType<$TSFixMe>) => {
    // @ts-expect-error TS7016 TODO: distructured obj needs to be typed
    const Delegator = ({ isEnterprise, router, ...props }) => {
      const isCustomSSO = Boolean(router.location.query.authProvider);
      const isRedirectingToDegree = router.location.query.redirectTo?.includes('degree');

      return isEnterprise || isCustomSSO || isRedirectingToDegree ? (
        <WrappedComponent {...props} isEnterprise={isEnterprise} router={router} />
      ) : (
        <AlternativeAuthenticationModal {...props} onCloseRedirectURL={props.redirectOnCloseUrl} />
      );
    };

    const displayName = WrappedComponent.displayName || WrappedComponent.name;
    Delegator.displayName = `Delegator(${displayName})`;

    hoistNonReactStatics(Delegator, WrappedComponent);

    return Delegator;
  }
)(AuthenticationModal);
